import { useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	Paper,
	Skeleton,
	Stack,
	Typography,
} from "@mui/material";
import {
	ArrowLeft,
	ClipboardList,
	Home,
	RefreshCcw,
	Trophy,
} from "lucide-react";

import { getPracticeSessionResults } from "utils/supabase";
import { useAuth } from "utils/AuthContext.js";
import { capitalizeWords } from "utils/text.js";

import ResultadosExamen from "components/Custom/ResultadosExamen.js";
import CardDesempenoEspecialidad from "components/Cards/CardDesempenoEspecialidad";
import CardFortalezas from "components/Custom/CardFortalezas";
import CardNavPreguntas from "components/Custom/CardNavPreguntas";

function normalizeNavigatorStatus(item) {
	const rawStatus = String(item?.status || item?.result_status || item?.answer_status || "")
		.trim()
		.toLowerCase();
	const explicitCorrect = item?.is_correct ?? item?.correct ?? item?.was_correct ?? item?.answered_correctly ?? null;
	const explicitIncorrect =
		item?.is_incorrect ?? item?.incorrect ?? item?.was_incorrect ?? item?.answered_incorrectly ?? null;

	if (rawStatus === "correct" || explicitCorrect === true || rawStatus === "answered_correctly" || rawStatus === "right") {
		return "correct";
	}

	if (
		rawStatus === "incorrect" ||
		explicitIncorrect === true ||
		explicitCorrect === false ||
		rawStatus === "answered_incorrectly" ||
		rawStatus === "wrong" ||
		rawStatus === "failed"
	) {
		return "incorrect";
	}

	if (
		rawStatus === "answered" ||
		rawStatus === "reviewed" ||
		rawStatus === "attempted" ||
		rawStatus === "submitted"
	) {
		return "answered";
	}

	const hasAnswerSignal =
		((item?.selected_option_id !== undefined && item?.selected_option_id !== null) ||
		(item?.option_id !== undefined && item?.option_id !== null) ||
		(item?.answer_option_id !== undefined && item?.answer_option_id !== null) ||
		(item?.response_option_id !== undefined && item?.response_option_id !== null) ||
		Boolean(item?.answer_text || item?.response_text || item?.user_answer || item?.answered_at || item?.answered_at_timestamp));

	return hasAnswerSignal ? "answered" : "unanswered";
}

function normalizePracticeResultsPayload(rawPayload) {
	const payload = Array.isArray(rawPayload) ? rawPayload[0] : rawPayload;
	if (!payload || typeof payload !== "object") return null;

	const summary = payload.summary || {};
	const navigatorRaw = payload.navigator || payload.question_navigator || [];
	const navigator = Array.isArray(navigatorRaw)
		? navigatorRaw.map((item) => ({
				...item,
				normalizedStatus: normalizeNavigatorStatus(item),
		  }))
		: [];
	const latestNavigatorByKey = new Map();
	navigator.forEach((item, index) => {
		const itemQuestionId = item?.question_id;
		const itemPosition = Number(item?.position);
		const dedupeKey =
			itemQuestionId !== undefined && itemQuestionId !== null
				? `q:${itemQuestionId}`
				: Number.isFinite(itemPosition) && itemPosition > 0
					? `p:${itemPosition}`
					: `i:${index}`;

		latestNavigatorByKey.set(dedupeKey, { ...item, __lastIndex: index });
	});

	const uniqueNavigator = Array.from(latestNavigatorByKey.values())
		.sort((a, b) => a.__lastIndex - b.__lastIndex)
		.map(({ __lastIndex, ...item }) => item);
	const baseNavigator = (uniqueNavigator.length > 0 ? uniqueNavigator : navigator)
		.map((item, index) => {
			const numericPosition = Number(item?.position);
			return {
				...item,
				position: Number.isFinite(numericPosition) && numericPosition > 0 ? numericPosition : index + 1,
				normalizedStatus: item?.normalizedStatus || normalizeNavigatorStatus(item),
			};
		})
		.sort((a, b) => Number(a?.position) - Number(b?.position));

	const summaryTotalQuestions = Number(summary.total_questions || 0);
	const navigatorMaxPosition = baseNavigator.reduce((max, item) => Math.max(max, Number(item?.position) || 0), 0);
	const expectedQuestionCount = Math.max(summaryTotalQuestions, baseNavigator.length, navigatorMaxPosition);
	const missingQuestionsCount = Math.max(0, expectedQuestionCount - baseNavigator.length);
	const nextPosition = navigatorMaxPosition > 0 ? navigatorMaxPosition + 1 : baseNavigator.length + 1;
	const syntheticUnansweredItems = Array.from({ length: missingQuestionsCount }).map((_, index) => ({
		question_id: null,
		position: nextPosition + index,
		normalizedStatus: "unanswered",
		status: "unanswered",
		is_synthetic_unanswered: true,
	}));
	const statsNavigator = [...baseNavigator, ...syntheticUnansweredItems].sort(
		(a, b) => Number(a?.position) - Number(b?.position)
	);
	const performanceRaw =
		payload.performance_by_specialty || payload.performance_by_speciality || [];
	const performanceBySpecialty = Array.isArray(performanceRaw)
		? performanceRaw.map((item) => {
				const correctAnswers = Number(item?.correct_answers ?? item?.correct_items ?? 0);
				const incorrectAnswers = Number(item?.incorrect_answers ?? item?.incorrect_items ?? 0);
				const explicitUnanswered = Number(item?.unanswered_questions ?? item?.unanswered_items ?? 0);
				const explicitTotal = Number(item?.total_questions ?? item?.total_items ?? item?.total_answered ?? 0);
				const resolvedTotal = Math.max(
					0,
					Number.isFinite(explicitTotal)
						? Math.max(explicitTotal, correctAnswers + incorrectAnswers + Math.max(0, explicitUnanswered))
						: correctAnswers + incorrectAnswers + Math.max(0, explicitUnanswered)
				);
				const unresolvedFromTotals = Math.max(0, resolvedTotal - correctAnswers - incorrectAnswers);
				const unansweredQuestions = Math.max(0, Number.isFinite(explicitUnanswered) ? explicitUnanswered : 0, unresolvedFromTotals);

				return {
					...item,
					specialty_section: item?.specialty_section || item?.section || "Sin sección",
					correct_answers: correctAnswers,
					incorrect_answers: incorrectAnswers,
					unanswered_questions: unansweredQuestions,
					total_questions: resolvedTotal,
				};
		  })
		: [];

	const hasNavigatorStats = statsNavigator.length > 0;
	const totalQuestionsFromNavigator = statsNavigator.length;
	const correctAnswers = statsNavigator.filter((item) => item.normalizedStatus === "correct").length;
	const incorrectAnswers = statsNavigator.filter((item) => item.normalizedStatus === "incorrect").length;
	const summaryCorrectAnswers = Number(summary.correct_answers || 0);
	const summaryIncorrectAnswers = Number(summary.incorrect_answers || 0);
	const summaryUnansweredQuestions = Number(summary.unanswered_questions || 0);

	const totalQuestions = hasNavigatorStats
		? Math.max(totalQuestionsFromNavigator, summaryTotalQuestions)
		: summaryTotalQuestions;
	const resolvedCorrectAnswers = hasNavigatorStats
		? Math.max(correctAnswers, summaryCorrectAnswers)
		: summaryCorrectAnswers;
	const resolvedIncorrectAnswers = hasNavigatorStats
		? Math.max(incorrectAnswers, summaryIncorrectAnswers)
		: summaryIncorrectAnswers;
	const unansweredFromTotals = Math.max(0, totalQuestions - resolvedCorrectAnswers - resolvedIncorrectAnswers);
	const unansweredQuestions = Math.max(summaryUnansweredQuestions, unansweredFromTotals);
	const unresolvedInPerformance = Math.max(
		0,
		unansweredQuestions - performanceBySpecialty.reduce((acc, item) => acc + (Number(item?.unanswered_questions) || 0), 0)
	);

	if (unresolvedInPerformance > 0) {
		performanceBySpecialty.push({
			specialty_id: "unresolved-unanswered",
			specialty_name: "Sin responder",
			specialty_section: "Sin sección",
			correct_answers: 0,
			incorrect_answers: 0,
			unanswered_questions: unresolvedInPerformance,
			total_questions: unresolvedInPerformance,
			accuracy_percentage: 0,
		});
	}

	return {
		...payload,
		navigator: statsNavigator,
		performance_by_specialty: performanceBySpecialty,
		summary: {
			...summary,
			total_questions: hasNavigatorStats
				? totalQuestions
				: Number(summary.total_questions || 0),
			correct_answers: hasNavigatorStats
				? resolvedCorrectAnswers
				: Number(summary.correct_answers || 0),
			incorrect_answers: hasNavigatorStats
				? resolvedIncorrectAnswers
				: Number(summary.incorrect_answers || 0),
			unanswered_questions: hasNavigatorStats
				? unansweredQuestions
				: Number(summary.unanswered_questions || 0),
			time_used_seconds:
				summary.time_used_seconds ?? summary.elapsed_time_seconds ?? 0,
		},
		summaryText:
			payload?.feedback?.message ||
			summary?.status_message ||
			"Sigue practicando, cada sesión suma.",
	};
}

export default function PracticaResultados() {
	const { sessionId } = useParams();
	const history = useHistory();
	const { user } = useAuth();

	const [resultsPayload, setResultsPayload] = useState(null);
	const [isLoading, setIsLoading] = useState(true);
	const [errorMessage, setErrorMessage] = useState("");

	const questionNavRef = useRef(null);

	useEffect(() => {
		window.scrollTo({ top: 0, left: 0, behavior: "auto" });
	}, [sessionId]);

	useEffect(() => {
		let isMounted = true;

		async function fetchPracticeResults() {
			setIsLoading(true);
			setErrorMessage("");

			try {
				const { data, error } = await getPracticeSessionResults(sessionId);
				if (error) throw error;
				const normalized = normalizePracticeResultsPayload(data);
				if (isMounted) {
					setResultsPayload(normalized);
				}
			} catch (error) {
				console.error("Error fetching practice results payload:", error);
				if (isMounted) {
					setErrorMessage(
						error?.message || "No fue posible cargar los resultados de la práctica."
					);
				}
			} finally {
				if (isMounted) {
					setIsLoading(false);
				}
			}
		}

		fetchPracticeResults();

		return () => {
			isMounted = false;
		};
	}, [sessionId]);

	const feedback = useMemo(() => resultsPayload?.feedback || null, [resultsPayload]);

	const handleQuestionSelect = (item) => {
		if (!item?.question_id) return;
		history.push(`/admin/practica/${sessionId}/preguntas/${item.question_id}`);
	};

	const handleReviewQuestions = () => {
		history.push(`/admin/practica/${sessionId}/preguntas`);
	};

	const handleRetryPractice = () => {
		history.push("/admin/practica");
	};

	const handleGoDashboard = () => {
		history.push("/admin/dashboard");
	};

	const handleBackToPractice = () => {
		history.push("/admin/practica");
	};

	if (isLoading) {
		return (
			<div className="w-full px-4 py-10">
				<Paper
					elevation={0}
					sx={{
						position: "relative",
						overflow: "hidden",
						border: "1px solid #dbeafe",
						borderRadius: "16px",
						background: "linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)",
						p: { xs: 2, sm: 2.5 },
					}}
				>
					<Box
						sx={{
							position: "absolute",
							top: -80,
							right: -50,
							width: 220,
							height: 220,
							borderRadius: "9999px",
							background: "radial-gradient(circle, rgba(37,99,235,0.18) 0%, rgba(37,99,235,0) 72%)",
							pointerEvents: "none",
						}}
					/>

					<Stack spacing={2.2}>
						<Stack direction="row" spacing={1.5} alignItems="center">
							<Box
								sx={{
									width: 42,
									height: 42,
									borderRadius: "12px",
									border: "1px solid #bfdbfe",
									backgroundColor: "#eff6ff",
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
								}}
							>
								<CircularProgress size={20} thickness={5} sx={{ color: "#2563eb" }} />
							</Box>
							<Box>
								<Typography sx={{ color: "#0f172a", fontSize: 15, fontWeight: 700 }}>
									Preparando tus resultados
								</Typography>
								<Typography sx={{ color: "#64748b", fontSize: 13 }}>
									Cargando resumen, estadísticas y navegación...
								</Typography>
							</Box>
						</Stack>

						<Box sx={{ display: "grid", gap: 1.2, gridTemplateColumns: { xs: "1fr", md: "1.4fr 1fr" } }}>
							<Skeleton variant="rounded" height={118} sx={{ borderRadius: "12px", bgcolor: "#eaf2ff" }} />
							<Skeleton variant="rounded" height={118} sx={{ borderRadius: "12px", bgcolor: "#f1f5f9" }} />
						</Box>

						<Box sx={{ display: "grid", gap: 1.1 }}>
							<Skeleton variant="rounded" height={14} width="86%" sx={{ bgcolor: "#eef2ff" }} />
							<Skeleton variant="rounded" height={14} width="72%" sx={{ bgcolor: "#e2e8f0" }} />
							<Skeleton variant="rounded" height={14} width="78%" sx={{ bgcolor: "#e2e8f0" }} />
						</Box>
					</Stack>
				</Paper>
			</div>
		);
	}

	if (errorMessage) {
		return (
			<div className="w-full px-4 py-6">
				<Paper elevation={0} sx={{ border: "1px solid #fecaca", borderRadius: "12px", p: 2 }}>
					<Stack spacing={2}>
						<Alert severity="error">{errorMessage}</Alert>
						<Box>
							<Button
								variant="outlined"
								startIcon={<ArrowLeft size={16} />}
								onClick={handleBackToPractice}
								sx={{ textTransform: "none", fontWeight: 700 }}
							>
								Volver a práctica
							</Button>
						</Box>
					</Stack>
				</Paper>
			</div>
		);
	}

	return (
		<>
			<div className="flex flex-wrap mt-4">
				<div className="w-full mb-6 px-4">
					<Stack direction={{ xs: "column", sm: "row" }} spacing={1.25} alignItems={{ xs: "stretch", sm: "center" }}>
						<Button
							variant="outlined"
							startIcon={<ArrowLeft size={16} />}
							onClick={handleBackToPractice}
							sx={{ textTransform: "none", fontWeight: 700, borderColor: "#93c5fd", color: "#1d4ed8" }}
						>
							Volver a práctica
						</Button>
					</Stack>
				</div>

				<div className="w-full mb-8 px-4">
					<ResultadosExamen
						resultsPayload={resultsPayload}
						user={capitalizeWords(user?.user_metadata?.full_name, true) || capitalizeWords(resultsPayload?.user_name, true) || "Usuario"}
					/>
				</div>

				{feedback && (
					<div className="w-full mb-6 px-4">
						<Paper
							elevation={0}
							sx={{
								border: "1px solid #dbeafe",
								borderRadius: "14px",
								backgroundColor: "#f8fbff",
								p: { xs: 1.5, sm: 2 },
							}}
						>
							<Stack direction="row" spacing={1.25} alignItems="flex-start">
								<Box sx={{ color: "#2563eb", mt: 0.1 }}>
									<Trophy size={20} />
								</Box>
								<Box>
									<Typography sx={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>
										{feedback?.title || "Resumen de desempeño"}
									</Typography>
									<Typography sx={{ mt: 0.4, fontSize: 13, color: "#475569" }}>
										{feedback?.message}
									</Typography>
								</Box>
							</Stack>
						</Paper>
					</div>
				)}

				<div className="w-full lg:w-4/12 mb-6 px-4">
					<CardDesempenoEspecialidad resultsPayload={resultsPayload} />
				</div>
				<div className="w-full lg:w-4/12 mb-6 px-4">
					<CardFortalezas resultsPayload={resultsPayload} />
				</div>
				<div className="w-full lg:w-4/12 mb-6 px-4" ref={questionNavRef}>
					<CardNavPreguntas
						resultsPayload={resultsPayload}
						onQuestionSelect={handleQuestionSelect}
					/>
				</div>

				<div className="w-full px-4 mb-8">
					<Paper
						elevation={0}
						sx={{
							width: "100%",
							border: "1px solid #dbeafe",
							borderRadius: "14px",
							p: { xs: 1.25, sm: 1.5 },
							backgroundColor: "#ffffff",
						}}
					>
						<Box
							sx={{
								display: "grid",
								gap: 1.25,
								gridTemplateColumns: {
									xs: "1fr",
									sm: "repeat(2, minmax(0, 1fr))",
									lg: "repeat(4, minmax(0, 1fr))",
								},
							}}
						>
							<Button
								variant="contained"
								onClick={handleReviewQuestions}
								startIcon={<ClipboardList size={18} />}
								sx={{
									textTransform: "none",
									fontWeight: 700,
									borderRadius: "10px",
									py: 1.1,
									bgcolor: "#2563eb",
									boxShadow: "none",
									"&:hover": {
										bgcolor: "#1d4ed8",
										boxShadow: "none",
									},
								}}
							>
								Revisar navegación
							</Button>

							<Button
								variant="outlined"
								onClick={handleRetryPractice}
								startIcon={<RefreshCcw size={18} />}
								sx={{
									textTransform: "none",
									fontWeight: 700,
									borderRadius: "10px",
									py: 1.1,
									borderColor: "#93c5fd",
									color: "#2563eb",
									"&:hover": {
										borderColor: "#60a5fa",
										backgroundColor: "#eff6ff",
									},
								}}
							>
								Nueva práctica
							</Button>

							<Button
								variant="outlined"
								onClick={handleBackToPractice}
								startIcon={<ArrowLeft size={18} />}
								sx={{
									textTransform: "none",
									fontWeight: 700,
									borderRadius: "10px",
									py: 1.1,
									borderColor: "#93c5fd",
									color: "#2563eb",
									"&:hover": {
										borderColor: "#60a5fa",
										backgroundColor: "#eff6ff",
									},
								}}
							>
								Volver a práctica
							</Button>

							<Button
								variant="outlined"
								onClick={handleGoDashboard}
								startIcon={<Home size={18} />}
								sx={{
									textTransform: "none",
									fontWeight: 700,
									borderRadius: "10px",
									py: 1.1,
									borderColor: "#93c5fd",
									color: "#2563eb",
									"&:hover": {
										borderColor: "#60a5fa",
										backgroundColor: "#eff6ff",
									},
								}}
							>
								Volver al dashboard
							</Button>
						</Box>
					</Paper>
				</div>
			</div>
		</>
	);
}
